import { createContext, useContext } from 'react';

/**
 * Where a library-rendered overlay should be portalled to — `null` for the document default.
 *
 * **This exists because of the top layer, and because `react-aria-components` cannot reach it.**
 * §4 and FS-3 FR-C2 draw the rule: a menu or tooltip opened inside a `<dialog>` must itself be in the
 * top layer, or it is trapped beneath its own dialog. `DropdownMenu` satisfies that natively — it
 * IS a `[popover]`, so it needs nothing from this module. Tooltip and Combobox cannot: the library
 * renders their overlays itself, and it FILTERS unknown DOM props, so a `popover` attribute passed
 * to `<Tooltip>` is silently dropped rather than applied (probe-verified at wave D — the attribute
 * simply does not appear on the rendered node). Nothing about that failure is visible: the tooltip
 * renders, positions, announces, and is invisible inside a modal.
 *
 * So the second route is taken instead. The library portals its overlays to `document.body` by
 * default, which is *outside* the dialog and therefore under it; portalling them INTO the dialog
 * puts them in the dialog's own subtree, which is already in the top layer. `Modal` publishes its
 * `<dialog>` here and the two library components read it — so the rule holds by construction
 * rather than by every future caller remembering it.
 *
 * The mechanism differs from §4's stated one (`[popover]`) while satisfying its requirement. That
 * divergence is recorded in the FS-3 plan rather than left for a reader to notice.
 */
export const OverlayContainerContext = createContext<Element | null>(null);

/**
 * The nearest overlay container, or `null` at the document level.
 *
 * `null` rather than `undefined` deliberately: the two consumers OMIT the library's
 * `UNSTABLE_portalContainer` prop entirely when there is no container, and a value that is already
 * `undefined` invites passing it straight through — which under `exactOptionalPropertyTypes` is a
 * different thing from omitting it, and is a type error at those call sites. Returning `null` makes
 * "there is no container" a value this module states rather than an absence a caller has to notice.
 */
export function useOverlayContainer(): Element | null {
    return useContext(OverlayContainerContext);
}
